import React, { FC, useEffect, useRef, SyntheticEvent } from 'react';

import { Typography, makeStyles } from '@material-ui/core';
import videojs from 'video.js';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { usePrevious } from '../../hooks';
import { Period } from '../../utils/types';

import 'video.js/dist/video-js.css';

dayjs.extend(duration);

const playerOptions = { autoplay: false };

const useStyles = makeStyles((theme) => ({
	fullWidth: {
		width: '100%'
	}
}));

const Player: FC<PlayerProps> = (props) => {
	const {
		clearEndTime,
		onTimeUpdate,
		onVideoFinished,
		periods = [],
		startPosition,
		endPosition,
		url,
		videoWidth = '512',
		videoHeight = '288',
		muted = true
	} = props;

	const previousUrl = usePrevious(url);

	const playerRef = useRef<HTMLVideoElement>(null);
	const classes = useStyles();

	const handlePlayerTimeUpdate = (e: SyntheticEvent): void => {
		const currentTime = playerRef.current?.currentTime;
		onTimeUpdate && onTimeUpdate(currentTime || 0);
		if (endPosition && currentTime && currentTime > endPosition) {
			const player = videojs(playerRef.current, undefined, () => {
				player.pause();
			});
		}
		if (currentTime && currentTime >= playerRef.current!.duration!) {
			onVideoFinished && onVideoFinished(url);
		}
	};

	useEffect(() => {
		if (url && url !== previousUrl) {
			//prettier-ignore
			const player = videojs(playerRef.current, playerOptions, () => {
                player.src(url);
                if (playerOptions.autoplay) { player.play(); }
                const textrack = (player.addTextTrack(
                    'captions',
                    'Play Details',
                    'en'
                ) as unknown) as TextTrack;
                textrack.mode = 'showing';
                periods.forEach((period) => {
                    const periodStart = period.startPosition
                        ? dayjs.duration(period.startPosition).asSeconds()
                        : null;
                    const periodEnd = period.endPosition
                        ? dayjs.duration(period.endPosition).asSeconds()
                        : null;
                    const gametime = period.startGameTime
                        ? dayjs
                            .unix(
                                dayjs
                                    .duration(period.startGameTime.position)
                                    .as('seconds')
                            )
                            .format('mm:ss')
                        : undefined;
                    const newcaption = `<b>${period.title || ''}</b> (${gametime || ''}) ${period.shortText || ''}`;
                    periodStart !== null &&
                        periodEnd &&
                        textrack.addCue(
                            new VTTCue(periodStart, periodEnd, newcaption)
                        );
                });
            });
		}
	}, [url, previousUrl, periods]);

	useEffect(() => {
		if (typeof startPosition !== 'undefined' && url) {
			const player = videojs(playerRef.current, undefined, () => {
				player.currentTime(startPosition || 0);
				player.play();
				const isMuted = localStorage.getItem('playerMuted') ? localStorage.getItem('playerMuted') === 'true' : false;
				player.muted(isMuted);
			});
		}
	}, [startPosition, url]);

	const handleVolumeChange = (e: SyntheticEvent): void => {
		const volume = playerRef?.current?.volume;
		const playerIsMuted = playerRef?.current?.muted;
		const muted = playerIsMuted || volume === 0;
		localStorage.setItem('playerMuted', muted.toString());
	};

	return url ? (
		<div className={classes.fullWidth} onClick={clearEndTime}>
			<video
				ref={playerRef}
				className={classes.fullWidth + ' video-js vjs-big-play-centered'}
				playsInline
				controls
				width={videoWidth}
				height={videoHeight}
				muted={muted}
				onTimeUpdate={handlePlayerTimeUpdate}
				onVolumeChange={handleVolumeChange}
			/>
		</div>
	) : (
		<Typography align='center'>No video asset found</Typography>
	);
};

type PlayerProps = {
	periods?: Period[];
	url: string;
	startPosition?: number;
	endPosition?: number | null;
	videoWidth?: string;
	videoHeight?: string;
	muted?: boolean;
	onTimeUpdate?: (position: number) => void;
	clearEndTime?: () => void;
	onVideoFinished?: (url: string) => void;
};

export default Player;
